import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LuInfo, LuPlus } from 'react-icons/lu';
import {
  ActionsPanel,
  ActionsTabs,
  ActionsTab,
  ActionsGrid,
  ActionCard,
  ActionTooltipSlot,
  ActionDisabledBadge,
  ActionIconWrapper,
  ActionLabel,
  ActionLockBadge,
  ActionUpgradeButton,
  CreateOfferLink,
  OfferCreateLinkRow,
} from './FlowBuilder.styles';
import { IoCloseCircleOutline, IoListOutline } from 'react-icons/io5';
import { SiGooglegemini } from 'react-icons/si';
import { FaLinkedin, FaLinkedinIn, FaLock } from 'react-icons/fa';
import { IoSettingsSharp } from 'react-icons/io5';
import { TbHourglassEmpty } from 'react-icons/tb';
import { LuMail, LuThumbsUp, LuSquareDashedBottomCode } from 'react-icons/lu';
import { AiOutlineIdcard, AiOutlineNodeIndex, AiOutlineUser } from 'react-icons/ai';
import { HiOutlineClock, HiOutlineSpeakerWave } from 'react-icons/hi2';
import { BiCommentDetail, BiMessageRoundedDetail, BiSearchAlt2 } from 'react-icons/bi';
import { MdOutlineApi, MdOutlineCall, MdOutlineCompareArrows, MdOutlineTaskAlt } from 'react-icons/md';
import { RiSendPlaneFill } from 'react-icons/ri';
import { GoChecklist } from 'react-icons/go';
import { FaLock as FaLockSolid } from 'react-icons/fa';
import { PiPlugsConnected, PiUsersDuotone } from 'react-icons/pi';
import PricingSection from '../atoms/Pricing/PricingSection';
import ModalContainer from '../molecules/ModalContainer';
import { getDisabledActionTitle, isActionNodeDisabled } from './constants';
import useFixedTooltip from '../../hooks/useFixedTooltip';
import FixedTooltipPortal from '../molecules/FixedTooltipPortal';
import { ActionDisabledTooltip } from './FlowBuilder.styles';

const TAB_META = [
  { id: 'email', label: 'Email', icon: <RiSendPlaneFill /> },
  { id: 'linkedin', label: 'LinkedIn', icon: <FaLinkedin /> },
  { id: 'automation', label: 'Automation', icon: <IoSettingsSharp /> },
  { id: 'conditions', label: 'Conditions', icon: <AiOutlineNodeIndex /> },
];

export const CreateOfferHint = () => {
  const navigate = useNavigate();

  return (
    <OfferCreateLinkRow>
      <CreateOfferLink type="button" onClick={() => navigate('/create-offer')}>
        <LuPlus />
        Create a new offer
      </CreateOfferLink>
    </OfferCreateLinkRow>
  );
};

const FlowBuilderActionsPanel = ({
  open,
  actionNodes,
  activeAddButtonId,
  nodes,
  userStatus,
  hasLinkedInProfiles = true,
  hasEmailAccounts = true,
  addNode,
  setActionsPanelOpen,
  setActiveAddButtonId,
}) => {
  const [activeTab, setActiveTab] = React.useState('email');
  const {
    containerRef: gridRef,
    showTooltip,
    hideTooltip,
    activeContent: activeHint,
    tooltipVisible,
    tooltipPos,
  } = useFixedTooltip([activeTab, open]);
  const disableOptions = { hasLinkedInProfiles, hasEmailAccounts };
  const actionByType = React.useMemo(
    () => Object.fromEntries((actionNodes || []).map(action => [action.type, action])),
    [actionNodes],
  );

  const itemsByTab = React.useMemo(
    () => ({
      email: [
        { key: 'send_email', label: 'Send Email', icon: <LuMail />, type: 'send_email' },
        { key: 'send_ai_email', label: 'Send AI Email', icon: <SiGooglegemini className="gradient-icon" />, type: 'send_ai_email' },
        { key: 'find_email', label: 'Find Email', icon: <BiSearchAlt2 />, type: "email_finder" },
      ],
      linkedin: [
        { key: 'visit_profile', label: 'Visit Profile', icon: <AiOutlineIdcard color="#4DDBBA" />, type: 'visit_profile' },
        { key: 'follow_user', label: 'Follow Profile', icon: <AiOutlineUser color="#4ABFFF" />, type: 'follow_user' },
        { key: 'send_connection', label: 'Send Request Connection', icon: <PiPlugsConnected color="#FE8888" />, type: 'send_connection' },
        { key: 'wait_for_connection', label: 'Wait for Acceptance', icon: <PiPlugsConnected color="#FE8888" />, type: 'wait_for_connection' },
        { key: 'send_message', label: 'Send LinkedIn Message', icon: <LuMail />, type: 'send_message' },
        { key: 'send_voice_message', label: 'Send Voice Message', icon: <HiOutlineSpeakerWave color="#4DDBBA" />, type: 'send_voice_message' },
        { key: 'send_linkedin_ai_message', label: 'Send AI LinkedIn Message', icon: <SiGooglegemini className="gradient-icon" />, type: 'send_linkedin_ai_message' },
        { key: 'like_post', label: 'Like Latest Post', icon: <LuThumbsUp color="#FAA666" />, type: 'like_post' },
        // { key: 'wait_for_connection', label: 'Wait for Acceptance', icon: <HiOutlineClock color="#FAA666" />, type: 'wait_for_connection' },
        { key: 'comment_post', label: 'Comment Latest Post', icon: <BiCommentDetail color="#4ABFFF" />, type: 'comment_post' },
        { key: 'withdraw_connection', label: 'Withdraw Connection Request', icon: <IoCloseCircleOutline color="#4DDBBA" />, locked: true },
        { key: 'send_in_mail', label: 'Send in Mail', icon: <LuMail />, locked: true },
      ],
      automation: [
        { key: 'delay', label: 'Delay', icon: <TbHourglassEmpty color="#2783EC" />, type: 'delay' },
        // { key: 'add_to_list', label: 'Add to List', icon: <IoListOutline />, locked: true },
        // { key: 'change_status', label: 'Change Status', icon: <GoChecklist />, locked: true },
        { key: 'send_to_another_automation', label: 'Sent To Another Automation', icon: <LuSquareDashedBottomCode />, locked: true },
        // { key: 'create_task', label: 'Create Task', icon: <MdOutlineTaskAlt color="#4DDBBA" />, locked: true },

        // { key: 'call_api', label: 'Call API', icon: <MdOutlineApi />, locked: true },
      ],
      conditions: [
        // { key: 'has_email_address', label: 'Has Email Address', icon: <LuMail />, locked: true },
        // { key: 'has_phone_number', label: 'Has Phone Number', icon: <MdOutlineCall />, locked: true },
        { key: 'check_email_status', label: 'Check Email Status', icon: <LuMail />, type: 'check_email_status' },
        { key: 'meeting_booked', label: 'Meeting Booked', icon: <BiMessageRoundedDetail />, locked: true },
        { key: 'check_if_connected', label: 'Check if Connected', icon: <PiPlugsConnected color="#FE8888" />, type: "check_connection" },
        // { key: 'has_linkedin_url', label: 'Has LinkedIn URL', icon: <FaLinkedinIn />, locked: true },
        { key: 'stripe_for_conversation', label: 'Stripe for Conversion', icon: <PiUsersDuotone />, type: "check_stripe_customer", locked: true },
        { key: 'ab_test', label: 'A/B Test', icon: <MdOutlineCompareArrows />, locked: true },
        { key: 'check_linkedin_message_status', label: 'Check LinkedIn Message Status', icon: <MdOutlineCompareArrows />, locked: true, type: 'check_linkedin_message_status' },
      ],
    }),
    [],
  );

  const visibleItems = itemsByTab[activeTab] || [];

  const resolveInsertPosition = () => {
    let insertIndex = null;
    let branchInfo = null;

    if (activeAddButtonId === 'main') {
      insertIndex = 0;
    } else if (activeAddButtonId && activeAddButtonId.startsWith('after-')) {
      const afterNodeId = activeAddButtonId.replace('after-', '');
      const idx = nodes.findIndex(n => n.id === afterNodeId);
      if (idx !== -1) insertIndex = idx + 1;
    } else {
      const BRANCHES = ['yes', 'no', 'replied', 'clicked', 'opened', 'nothing'];
      let matched = false;
      for (const b of BRANCHES) {
        const preMatch = activeAddButtonId?.match(new RegExp(`^${b}-(.+)-pre-(\\d+)$`));
        if (preMatch) { branchInfo = { parentNodeId: preMatch[1], branch: b, branchInsertIndex: parseInt(preMatch[2], 10) }; matched = true; break; }
        const atMatch = activeAddButtonId?.match(new RegExp(`^${b}-(.+)-at-(\\d+)$`));
        if (atMatch) { branchInfo = { parentNodeId: atMatch[1], branch: b, branchInsertIndex: parseInt(atMatch[2], 10) + 1 }; matched = true; break; }
      }
      if (!matched) {
        for (const b of BRANCHES) {
          if (activeAddButtonId?.startsWith(`${b}-`)) {
            branchInfo = { parentNodeId: activeAddButtonId.slice(b.length + 1), branch: b };
            break;
          }
        }
      }
    }

    return { insertIndex, branchInfo };
  };

  return (
    <ActionsPanel $open={open}>
      <ActionsTabs>
        {TAB_META.map(tab => (
          <ActionsTab key={tab.id} $active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
            {tab.icon}
            {tab.label}
          </ActionsTab>
        ))}
      </ActionsTabs>
      <ActionsGrid ref={gridRef}>
        {visibleItems.map(item => {
          const action = item.type ? actionByType[item.type] : null;
          // const isTrialLocked = userStatus === 'trial_active';
          const isTrialLocked = false;

          let workflowDisabled = false;
          let hint = item.locked ? 'Coming Soon' : null;

          if (!isTrialLocked && !item.locked) {
            // if (!hasLinkedInProfiles) {
            //   workflowDisabled = true;
            //   hint = 'Connect a LinkedIn account to add steps to your workflow.';
            // } else
            if (action) {
              workflowDisabled = isActionNodeDisabled(action, nodes, disableOptions);
              hint = workflowDisabled
                ? getDisabledActionTitle(action, actionNodes, disableOptions)
                : null;
            }
          }

          const isUnavailable = Boolean(item.locked) || (Boolean(item.type) && !action);
          const disabled = isTrialLocked || workflowDisabled || isUnavailable;
          const isMuted = disabled && !isTrialLocked;

          return (
            <ActionCard
              key={item.key}
              $locked={isTrialLocked}
              $disabled={disabled && !isTrialLocked}
              draggable={!disabled && Boolean(action)}
              onMouseEnter={e => {
                if (!hint) return;
                const anchor = e.currentTarget.querySelector('[data-tooltip-anchor]');
                if (anchor) showTooltip(anchor, hint);
              }}
              onMouseLeave={() => {
                if (hint) hideTooltip();
              }}
              onDragStart={e => {
                if (disabled || !action) return;
                e.dataTransfer.setData('nodeType', action.type);
              }}
              onClick={() => {
                if (disabled || !action) return;
                const { insertIndex, branchInfo } = resolveInsertPosition();
                addNode(action.type, insertIndex, branchInfo);
                setActionsPanelOpen(false);
                setActiveAddButtonId(null);
              }}
            >
              {isTrialLocked ? (
                <ActionLockBadge>
                  <FaLock />
                </ActionLockBadge>
              ) : null}
              {!isTrialLocked && workflowDisabled ? (
                <ActionDisabledBadge aria-hidden>
                  <LuInfo />
                </ActionDisabledBadge>
              ) : null}
              <ActionIconWrapper
                $bgColor={action?.bgColor}
                $color={isTrialLocked ? '#9ca3af' : (action?.color || '#317bff')}
                $muted={isMuted}
              >
                {item.icon}
              </ActionIconWrapper>
              <ActionLabel $muted={isMuted}>{item.label}</ActionLabel>
              {isTrialLocked ? (
                <ModalContainer
                  newStyle
                  width={1200}
                  btnComponent={({ onClick }) => (
                    <ActionUpgradeButton className="upgrade-cta" type="button" onClick={onClick}>
                      <FaLockSolid />
                      Get Pro
                    </ActionUpgradeButton>
                  )}
                  content={({ onClose }) => <PricingSection onClose={onClose} />}
                />
              ) : null}
              {hint ? <ActionTooltipSlot data-tooltip-anchor aria-hidden /> : null}
            </ActionCard>
          );
        })}
      </ActionsGrid>
      <FixedTooltipPortal
        content={activeHint}
        visible={tooltipVisible}
        position={tooltipPos}
        tooltipComponent={ActionDisabledTooltip}
      />
    </ActionsPanel>
  );
};

export default FlowBuilderActionsPanel;
